import config from 'config'
import Bugsnag from '@bugsnag/js'

import debug from 'util/debug'
import storage from 'util/storage'
import uuid from 'util/uuid'
import { throttle } from 'util/functions'
import {
  getAudioNotificationsState,
  getAudioNotificationsStateTs,
  setAudioNotificationsState,
  setMatrixAudioNotificationState,
} from 'util/audio'
import { isWebPushSupported } from 'util/browser'
import { isGranted } from 'util/webPush/permissions'
import { getSubscription } from 'util/webPush/subscriptions'

import { selectAccountPreferenceChatEnabled } from 'selectors/app/selectAccountPreferences'

// Matrix includes
import MatrixClientPeg from 'matrix-react-sdk/lib/MatrixClientPeg'
import * as Lifecycle from 'matrix-react-sdk/lib/Lifecycle'
import Presence from 'matrix-react-sdk/lib/Presence'
import SettingsStore from 'matrix-react-sdk/lib/settings/SettingsStore'

import { doSubscribe } from 'actions/webPush/doSubscribe'
import { doFetchWidgetById } from 'ducks/widgets/operations'
import { doUpdatePresence, doUpdatePresenceSettings } from './actions/presence'
import GroovePresence from './Presence'
import './IndexedDb.worker'
import { BULK_USER_PRESENCE_CHANGED } from './actionTypes/presence'
import { MATRIX_CLIENT_FAILED, MATRIX_CLIENT_READY } from './actionTypes/client'

window.mxSettingsStore = SettingsStore

const {
  chatServerUrl: MATRIX_CHAT_SERVER_URL,
  chatIdentityServerUrl: MATRIX_IDENTITY_SERVER_URL,
} = config
let presenceEvents = []

export class MatrixEnvironment {
  constructor(storeInstance) {
    this.store = storeInstance
    this.loadingPromise = null
    MatrixClientPeg.setIndexedDbWorkerScript('/chat-sw.js')
    MatrixClientPeg.setCreateOptions({
      storeName: `groove-chat`,
      // For some reason the matrix-js-sdk prefixes the normal index db store
      // with matrix-js-sdk but not the crypto store. Adding the prefix here
      // to keep it consistent
      cryptoStoreName: `matrix-js-sdk:groove-chat-crypto`,
    })
    Presence.setPresenceProvider(GroovePresence)
    this.loadMatrix().catch(() => {})
  }

  isChatEnabled() {
    const { getState } = this.store
    return selectAccountPreferenceChatEnabled(getState())
  }

  loadMatrix() {
    if (!this.isChatEnabled()) return Promise.resolve(null)
    if (this.loadingPromise) return this.loadingPromise
    this.loadingPromise = this.loadMatrixAttempt()
    return this.loadingPromise
  }

  async loadMatrixAttempt() {
    const { dispatch } = this.store
    try {
      this.startupPhase = 'session'
      await this.setupLoadedSession()
      this.startupPhase = 'presence'
      await this.loadPresenceFromServer()
      this.startupPhase = 'preSyncListeners'
      await this.subscribe()
      this.startupPhase = 'firstSync'
      await this.waitForFirstSync()
      this.startupPhase = 'postSyncListeners'
      await this.subscribePostFirstSync()
      const client = MatrixClientPeg.get()
      this.ensurePusherRegistration()
      this.syncAudioNotificationsState(
        client.getAccountData('g.notifications.audio'),
        true
      )
      // Setup the default matrix configuration settings
      this.presence = Presence.get()
      this.presence.initClient(client, this.onAccountPresence)
      dispatch({ type: MATRIX_CLIENT_READY })
      this.startupPhase = null
      return this
    } catch (error) {
      try {
        this.reportStartupFailure(error)
      } catch {
        // Reporting must not replace the startup error.
      }
      this.cleanupAttemptListeners()
      this.firstSyncPromise = null
      this.resolveFirstSync = null
      this.loadingPromise = null
      this.startupPhase = null
      dispatch({ type: MATRIX_CLIENT_FAILED })
      throw error
    }
  }

  reportStartupFailure(error) {
    const metadata = {
      phase: this.startupPhase,
      errorClass: error?.name,
      code: error?.code,
      status: error?.status || error?.statusCode,
    }
    Bugsnag.notify(new Error('Matrix client startup failed'), event => {
      event.addMetadata('matrixStartup', metadata)
    })
  }

  cleanupAttemptListeners() {
    let client
    try {
      client = MatrixClientPeg.get()
    } catch {
      return
    }
    if (!client) return
    ;[
      ['event', this.onPresenceEvent],
      ['accountData', this.onAccountData],
      ['event', this.onIntegrationEvent],
      ['sync', this.onSync],
    ].forEach(([eventName, listener]) => {
      try {
        client.off(eventName, listener)
      } catch {
        // Cleanup must not replace the startup error.
      }
    })
  }

  async setupLoadedSession() {
    let loadedSession = await Lifecycle.loadSession({
      fragmentQueryParams: {},
      enableGuest: false,
      guestHsUrl: MATRIX_CHAT_SERVER_URL,
      guestIsUrl: MATRIX_IDENTITY_SERVER_URL,
      defaultDeviceDisplayName: 'None',
    })
    if (!loadedSession) {
      const { chatId: userId, chatAccessToken: accessToken } =
        storage.get('currentUser') || {}
      if (userId && accessToken) {
        const credentials = {
          homeserverUrl: MATRIX_CHAT_SERVER_URL,
          identityServerUrl: MATRIX_IDENTITY_SERVER_URL,
          userId,
          accessToken,
          deviceId: uuid(),
        }
        MatrixClientPeg.replaceUsingCreds(credentials)
        await Lifecycle.doSetLoggedIn(credentials, false)
        loadedSession = true
      }
    }
    if (!loadedSession) {
      throw new Error(
        'CRITICAL: Unable to setup a loaded session. Loading for token and registration failed'
      )
    }
    return true
  }

  onAccountPresence = event => {
    const {
      unavailableTimeMs: autoAway,
      offlineTimeMs: autoOffline,
      state,
      autoPresence,
    } = event.getContent()
    const { dispatch } = this.store
    dispatch(doUpdatePresenceSettings({ autoAway, autoOffline, autoPresence }))
    dispatch(doUpdatePresence(state))
  }

  onAccountData = event => {
    if (event.getType() === 'g.notifications.audio') {
      this.syncAudioNotificationsState(event)
    }
  }

  onPresenceEvent = event => {
    if (event.getType() === 'm.presence') {
      this.schedulePresenceUpdate(event)
    }
  }

  onIntegrationEvent = event => {
    const eventType = event.getType()
    if (
      eventType === 'g.integration.bridge.disconnected' ||
      eventType === 'g.integration.facebook.disconnected' // legacy
    ) {
      const { dispatch } = this.store
      const { widget_id: widgetId } = event.getContent()
      dispatch(doFetchWidgetById(widgetId))
    }
  }

  onSync = state => {
    debug('MatrixClient sync state => %s', state)
    if (state === 'PREPARED' && this.resolveFirstSync) {
      this.resolveFirstSync()
      this.resolveFirstSync = null
      MatrixClientPeg.get().off('sync', this.onSync)
    }
  }

  async subscribe() {
    const client = MatrixClientPeg.get()
    client.on('event', this.onPresenceEvent)
  }

  async subscribePostFirstSync() {
    const client = MatrixClientPeg.get()
    client.on('accountData', this.onAccountData)
    client.on('event', this.onIntegrationEvent)
  }

  async loadPresenceFromServer() {
    const serverPresence = await MatrixClientPeg.get().getAccountDataFromServer(
      'g.presence'
    )
    const { state = 'online' } = serverPresence || {}
    MatrixClientPeg.updateSyncPresence(state)
  }

  schedulePresenceUpdate(event) {
    presenceEvents.push(event)

    // This function is debounced to only execute after 500ms. This
    // allows gives the buffer time for us to acumolate presence events
    // before syncing them to state
    this.updatePresenceInState()
  }

  updatePresenceInState = throttle(
    () => {
      this.store.dispatch({
        type: BULK_USER_PRESENCE_CHANGED,
        payload: presenceEvents.reduce((memo, evt) => {
          const userId = evt.getSender()
          const presence = evt.getContent().presence
          if (userId && presence) {
            // eslint-disable-next-line no-param-reassign
            memo.push({ userId, presence })
          }
          return memo
        }, []),
      })
      presenceEvents = []
    },
    500,
    { leading: false }
  )

  waitForFirstSync() {
    if (!this.firstSyncPromise) {
      this.firstSyncPromise = new Promise(resolve => {
        const client = MatrixClientPeg.get()
        this.resolveFirstSync = resolve
        client.on('sync', this.onSync)
      })
    }
    return this.firstSyncPromise
  }

  getSettingsStore() {
    return SettingsStore
  }

  async get() {
    await this.loadMatrix()
    return MatrixClientPeg.get()
  }

  // TODO (jscheel): Consider checkng this intermittently, not just on load
  async ensurePusherRegistration() {
    if (!isWebPushSupported() || !isGranted()) return
    const client = MatrixClientPeg.get()
    const { dispatch } = this.store
    const { pushers } = await client.getPushers()
    const currentPushSubscription = await getSubscription()
    let shouldResubscribe = false

    // NOTE (jscheel): If we somehow do not have a push subscription in the
    // browser, we need to try and set it back up.
    if (!currentPushSubscription || !currentPushSubscription.endpoint) {
      shouldResubscribe = true
    } else {
      // NOTE (jscheel): This is slightly problematic because we can't ensure that
      // the pusher is set up unless chat is loaded, but it's the best we can do
      // right now. We have to ensure that the pusher exists here becuase synapse
      // will delete the pusher if it fails. This can happen when the service worker
      // is uninstalled or deleted, because the browser will deregister the push
      // endpoint. If synapse pushes a notification through sygnal, and sygnal fails
      // to push the notification to the browser's non-existent push endpoint,
      // synapse will consider this a failure and delete the pusher.
      shouldResubscribe = !pushers.find(pusher => {
        const { app_id: appId, kind, data: { endpoint } = {} } = pusher
        return (
          appId === 'com.groovehq.app.web' &&
          kind === 'htttp' &&
          endpoint === currentPushSubscription.endpoint
        )
      })
    }

    if (shouldResubscribe) {
      dispatch(doSubscribe())
    }
  }

  syncAudioNotificationsState(mxEvent, initialSync = false) {
    if (!mxEvent) return

    const matrixEnabled = !!mxEvent.getContent()?.enabled
    const matrixTs = mxEvent.getContent()?.ts
    if (initialSync) {
      const lsEnabled = getAudioNotificationsState()
      const lsTs = getAudioNotificationsStateTs()
      if (matrixEnabled !== lsEnabled) {
        if (matrixTs > lsTs) {
          setAudioNotificationsState(matrixEnabled, matrixTs)
        } else {
          setMatrixAudioNotificationState(lsEnabled, lsTs)
        }
      }
      return
    }

    setAudioNotificationsState(matrixEnabled, matrixTs)
  }

  async logout() {
    Lifecycle.stopMatrixClient()
    await Lifecycle.clearStorage()
  }
}

export default new MatrixEnvironment(app.store)
